/**
 * Indicator rendering — price-pane overlays and stacked sub-panes.
 *
 * Two things here are easy to get wrong and both are handled explicitly:
 *
 *   1. **NaN is pen-up.** Indicator arrays are NaN through their warm-up
 *      window and wherever data is missing. Every polyline here breaks the
 *      path at a NaN rather than interpolating across it, so a 200-period EMA
 *      on a 210-bar chart draws ten points, not a line from zero.
 *   2. **Overlays participate in autoscale.** A Bollinger band or a slow EMA
 *      routinely sits outside the visible high/low range. `priceExtent` feeds
 *      those bounds back into the viewport so overlays are never silently
 *      clipped off the top of the chart.
 */

import { crisp } from '../lib/frame';
import { compact, niceStep } from '../lib/num';
import { C, F } from '../styles/palette';
import type { IndicatorOutput } from '../data/indicators';
import type { ChartViewport } from './scales';

export interface PaneRect {
  top: number;
  bottom: number;
  height: number;
}

/** Vertical slice of the sub-pane area belonging to sub-indicator `i`. */
export function subPaneRect(vp: ChartViewport, i: number, count: number): PaneRect {
  if (count <= 0) return { top: vp.subPaneTop, bottom: vp.subPaneTop, height: 0 };
  const total = vp.subPaneHeight;
  const each = total / count;
  const top = vp.subPaneTop + each * i;
  return { top, bottom: top + each, height: each };
}

/**
 * Min/max across every visible sample of every price-pane overlay.
 * Returns NaN bounds when nothing is drawable, which `autoFit` ignores.
 */
export function priceExtent(
  vp: ChartViewport,
  outputs: IndicatorOutput[],
  seriesLength: number,
): { min: number; max: number } {
  let min = Infinity;
  let max = -Infinity;
  const lo = Math.max(0, vp.firstVisible);
  const hi = Math.min(seriesLength - 1, vp.lastVisible);
  if (hi < lo) return { min: NaN, max: NaN };

  for (const out of outputs) {
    if (out.pane !== 'price') continue;
    for (const line of out.lines) {
      const v = line.values;
      const n = Math.min(hi, v.length - 1);
      for (let i = lo; i <= n; i++) {
        const x = v[i];
        if (Number.isNaN(x)) continue;
        if (x < min) min = x;
        if (x > max) max = x;
      }
    }
  }
  return {
    min: min === Infinity ? NaN : min,
    max: max === -Infinity ? NaN : max,
  };
}

/** Polyline over bar indices, breaking the path at NaN. */
function strokeSeries(
  ctx: CanvasRenderingContext2D,
  vp: ChartViewport,
  values: Float64Array,
  lo: number,
  hi: number,
  yOf: (v: number) => number,
): void {
  let pen = false;
  ctx.beginPath();
  for (let i = lo; i <= hi; i++) {
    const v = values[i];
    if (Number.isNaN(v)) {
      pen = false;
      continue;
    }
    const x = vp.xOf(i);
    const y = yOf(v);
    if (!pen) {
      ctx.moveTo(x, y);
      pen = true;
    } else {
      ctx.lineTo(x, y);
    }
  }
  ctx.stroke();
}

export function drawPriceOverlays(
  ctx: CanvasRenderingContext2D,
  vp: ChartViewport,
  outputs: IndicatorOutput[],
  seriesLength: number,
): void {
  const lo = Math.max(0, vp.firstVisible);
  const hi = Math.min(seriesLength - 1, vp.lastVisible);
  if (hi < lo) return;

  ctx.save();
  ctx.beginPath();
  ctx.rect(vp.plotLeft, vp.priceTop, vp.plotWidth, vp.priceHeight);
  ctx.clip();
  ctx.lineJoin = 'round';
  ctx.lineCap = 'butt';

  const yOf = (v: number) => vp.yOf(v);

  // Bands beneath lines, so the envelope edges stay crisp on top of the fill.
  for (const out of outputs) {
    if (out.pane !== 'price' || !out.band) continue;
    const upper = out.lines.find((l) => l.key === out.band!.upper);
    const lower = out.lines.find((l) => l.key === out.band!.lower);
    if (!upper || !lower) continue;

    ctx.fillStyle = out.band.fill;
    ctx.beginPath();
    let started = false;
    for (let i = lo; i <= hi; i++) {
      const v = upper.values[i];
      if (Number.isNaN(v)) continue;
      const x = vp.xOf(i);
      const y = yOf(v);
      if (!started) {
        ctx.moveTo(x, y);
        started = true;
      } else ctx.lineTo(x, y);
    }
    if (started) {
      for (let i = hi; i >= lo; i--) {
        const v = lower.values[i];
        if (Number.isNaN(v)) continue;
        ctx.lineTo(vp.xOf(i), yOf(v));
      }
      ctx.closePath();
      ctx.fill();
    }
  }

  for (const out of outputs) {
    if (out.pane !== 'price') continue;
    for (const line of out.lines) {
      ctx.strokeStyle = line.color;
      ctx.lineWidth = line.width;
      if (line.dashed) ctx.setLineDash([4, 3]);
      strokeSeries(ctx, vp, line.values, lo, Math.min(hi, line.values.length - 1), yOf);
      if (line.dashed) ctx.setLineDash([]);
    }
  }

  ctx.restore();
}

/**
 * One sub-pane: its own y scale, optional fixed range and guide levels, an
 * optional histogram, then its lines and a right-hand axis.
 */
export interface SubPaneHover {
  active: boolean;
  y: number;
  /** Bar index under the pointer, or -1. */
  index: number;
}

export function drawSubPane(
  ctx: CanvasRenderingContext2D,
  vp: ChartViewport,
  out: IndicatorOutput,
  rect: PaneRect,
  seriesLength: number,
  hover: SubPaneHover,
): void {
  if (rect.height < 14) return;
  const lo = Math.max(0, vp.firstVisible);
  const hi = Math.min(seriesLength - 1, vp.lastVisible);
  if (hi < lo) return;

  // Scale: fixed range if the indicator declares one (RSI is always 0..100),
  // otherwise fit the visible samples.
  let min: number;
  let max: number;
  if (out.range) {
    [min, max] = out.range;
  } else {
    min = Infinity;
    max = -Infinity;
    const scan = (v: Float64Array) => {
      const n = Math.min(hi, v.length - 1);
      for (let i = lo; i <= n; i++) {
        const x = v[i];
        if (Number.isNaN(x)) continue;
        if (x < min) min = x;
        if (x > max) max = x;
      }
    };
    for (const l of out.lines) scan(l.values);
    if (out.histogram) scan(out.histogram.values);
    if (min === Infinity) {
      min = 0;
      max = 1;
    }
    if (out.zeroLine) {
      // Keep zero centred so the histogram reads symmetrically.
      const m = Math.max(Math.abs(min), Math.abs(max)) || 1;
      min = -m;
      max = m;
    }
    if (max === min) {
      max += 1;
      min -= 1;
    }
    const pad = (max - min) * 0.12;
    min -= pad;
    max += pad;
  }

  const inner = rect.height - 4;
  const top = rect.top + 2;
  const yOf = (v: number) => top + inner - ((v - min) / (max - min || 1)) * inner;

  ctx.save();
  ctx.beginPath();
  ctx.rect(vp.plotLeft, rect.top, vp.plotWidth, rect.height);
  ctx.clip();

  // Guides (RSI 30/50/70) and the zero line.
  if (out.guides?.length) {
    ctx.strokeStyle = C.grid;
    ctx.setLineDash([3, 3]);
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (const g of out.guides) {
      const y = crisp(yOf(g));
      if (y < rect.top || y > rect.bottom) continue;
      ctx.moveTo(vp.plotLeft, y);
      ctx.lineTo(vp.plotRight, y);
    }
    ctx.stroke();
    ctx.setLineDash([]);
  }
  if (out.zeroLine) {
    const y = crisp(yOf(0));
    ctx.strokeStyle = C.gridStrong;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(vp.plotLeft, y);
    ctx.lineTo(vp.plotRight, y);
    ctx.stroke();
  }

  // Overbought/oversold zones. These cover roughly a third of the pane each,
  // so they have to be barely-there washes — at any real opacity the pane
  // reads as two blocks of colour with a line lost inside them.
  if (out.kind === 'rsi' && out.guides && out.guides.length >= 3) {
    const hiG = out.guides[out.guides.length - 1];
    const loG = out.guides[0];
    ctx.fillStyle = 'rgba(246,70,93,0.045)';
    ctx.fillRect(vp.plotLeft, yOf(max), vp.plotWidth, Math.max(0, yOf(hiG) - yOf(max)));
    ctx.fillStyle = 'rgba(46,189,133,0.045)';
    ctx.fillRect(vp.plotLeft, yOf(loG), vp.plotWidth, Math.max(0, yOf(min) - yOf(loG)));
  }

  if (out.histogram) {
    const v = out.histogram.values;
    const zero = yOf(0);
    const bw = Math.max(1, Math.floor(vp.barWidth * 0.6));
    const half = bw / 2;
    // Four passes: rising/falling on each side of zero. The fade colour marks
    // a bar that is shrinking, which is the early-warning signal traders read.
    for (let pass = 0; pass < 4; pass++) {
      const positive = pass < 2;
      const growing = pass % 2 === 0;
      ctx.fillStyle = positive
        ? growing
          ? out.histogram.upColor
          : out.histogram.upFade
        : growing
          ? out.histogram.downColor
          : out.histogram.downFade;
      ctx.beginPath();
      for (let i = lo; i <= Math.min(hi, v.length - 1); i++) {
        const x = v[i];
        if (Number.isNaN(x)) continue;
        if (x >= 0 !== positive) continue;
        const prev = i > 0 ? v[i - 1] : NaN;
        const isGrowing = Number.isNaN(prev) ? true : Math.abs(x) >= Math.abs(prev);
        if (isGrowing !== growing) continue;
        const y = yOf(x);
        const px = Math.round(vp.xOf(i) - half);
        const h = Math.max(1, Math.abs(y - zero));
        ctx.rect(px, Math.round(Math.min(y, zero)), bw, h);
      }
      ctx.fill();
    }
  }

  ctx.lineJoin = 'round';
  for (const line of out.lines) {
    ctx.strokeStyle = line.color;
    ctx.lineWidth = line.width;
    if (line.dashed) ctx.setLineDash([4, 3]);
    strokeSeries(ctx, vp, line.values, lo, Math.min(hi, line.values.length - 1), yOf);
    if (line.dashed) ctx.setLineDash([]);
  }
  ctx.restore();

  // Right-hand axis for the pane.
  ctx.font = F.micro;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = C.text3;
  const labels = out.range
    ? (out.guides ?? [min, max])
    : niceLevels(min, max, Math.max(2, Math.floor(rect.height / 26)));
  for (const lv of labels) {
    const y = yOf(lv);
    if (y < rect.top + 5 || y > rect.bottom - 3) continue;
    ctx.fillText(formatLevel(lv), vp.plotRight + 6, y);
  }

  // Pane label, reading the bar under the crosshair rather than always the
  // newest one. Hardcoding the last bar made the readout a static number that
  // ignored the cursor entirely — the pane looked inert to hover.
  const idx =
    hover.active && hover.index >= 0 && hover.index < seriesLength
      ? hover.index
      : seriesLength - 1;

  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.font = F.micro;
  let x = vp.plotLeft + 6;
  ctx.fillStyle = C.text2;
  ctx.fillText(out.label, x, rect.top + 3);
  x += ctx.measureText(out.label).width + 8;

  for (const line of out.lines) {
    const v = line.values[Math.min(idx, line.values.length - 1)];
    if (Number.isNaN(v)) continue;
    ctx.fillStyle = line.color;
    const s = formatLevel(v);
    ctx.fillText(s, x, rect.top + 3);
    x += ctx.measureText(s).width + 7;
  }
  if (out.histogram) {
    const hv = out.histogram.values[Math.min(idx, out.histogram.values.length - 1)];
    if (!Number.isNaN(hv)) {
      ctx.fillStyle = hv >= 0 ? out.histogram.upColor : out.histogram.downColor;
      ctx.fillText(formatLevel(hv), x, rect.top + 3);
    }
  }

  // Crosshair tag on this pane's own axis, so the pointer height reads as a
  // value here the same way it does over the price pane.
  if (hover.active && hover.y > rect.top && hover.y < rect.bottom) {
    const value = min + ((top + inner - hover.y) / (inner || 1)) * (max - min);
    const tagH = 14;
    const tagY = Math.round(hover.y - tagH / 2);
    ctx.fillStyle = C.crosshairLabel;
    ctx.fillRect(vp.plotRight + 1, tagY, vp.insets.right - 1, tagH);
    ctx.font = F.micro;
    ctx.fillStyle = C.text0;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(formatLevel(value), vp.plotRight + 6, tagY + tagH / 2 + 0.5);
  }
}

function niceLevels(min: number, max: number, count: number): number[] {
  const step = niceStep((max - min) / Math.max(1, count));
  const out: number[] = [];
  const start = Math.ceil(min / step) * step;
  for (let v = start; v <= max; v += step) out.push(Math.round(v / step) * step);
  return out;
}

function formatLevel(v: number): string {
  const a = Math.abs(v);
  if (a >= 1000) return compact(v);
  if (a >= 10) return v.toFixed(1);
  if (a >= 1) return v.toFixed(2);
  if (a === 0) return '0';
  return v.toFixed(4);
}

/**
 * Legend rows for price-pane overlays, drawn under the OHLC line. Returns the
 * y coordinate the next thing may use.
 */
export function drawOverlayLegend(
  ctx: CanvasRenderingContext2D,
  vp: ChartViewport,
  outputs: IndicatorOutput[],
  index: number,
  startY: number,
): number {
  let y = startY;
  ctx.font = F.micro;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';

  for (const out of outputs) {
    if (out.pane !== 'price') continue;
    let x = vp.plotLeft + 8;

    ctx.fillStyle = out.lines[0]?.color ?? C.text2;
    ctx.fillRect(x, y + 4, 7, 2);
    x += 11;

    ctx.fillStyle = C.text2;
    ctx.fillText(out.label, x, y);
    x += ctx.measureText(out.label).width + 7;

    for (const line of out.lines) {
      const i = Math.min(Math.max(0, index), line.values.length - 1);
      const v = line.values[i];
      if (Number.isNaN(v)) continue;
      ctx.fillStyle = line.color;
      const s = formatLevel(v);
      ctx.fillText(s, x, y);
      x += ctx.measureText(s).width + 6;
    }
    y += 12;
  }
  return y;
}
